import React, { useState, useEffect, useMemo } from 'react';
import { Box, Paper, Typography, Button, CircularProgress } from '@mui/material';
import { BRAND_GREEN_PRIMARY, DARKER_TEXT, DARK_TEXT, PLACEHOLDER_TEXT } from 'resources/constants/colors';
import useWellness from 'state/wellness';

// ---------------------------------------------------------------------------
// AI-powered contextual responses (static seed — ready for API integration)
// ---------------------------------------------------------------------------

const MOODS = [
    {
        emoji: '😣',
        label: 'Muy mal',
        value: 1,
        color: '#ef5350',
        bg: '#ffeaea',
        insight: {
            message: 'Gracias por compartir cómo te sientes. Eso toma valentía.',
            suggestion: 'Si estás pasando por un momento muy difícil, hablar con tu terapeuta puede ayudarte ahora.',
            cta: 'Contactar terapeuta',
            icon: '💙',
        },
    },
    {
        emoji: '😟',
        label: 'Mal',
        value: 2,
        color: '#ef8c35',
        bg: '#fff3e0',
        insight: {
            message: 'Está bien no estar bien. No tienes que cargarlo solo.',
            suggestion: 'Revisa tu próxima sesión o escríbele un mensaje a tu terapeuta.',
            cta: 'Ver mis citas',
            icon: '🌿',
        },
    },
    {
        emoji: '😐',
        label: 'Regular',
        value: 3,
        color: '#8e9b3e',
        bg: '#f5f7e8',
        insight: {
            message: 'Los días neutros también son parte del proceso.',
            suggestion: 'Un pequeño ejercicio de respiración puede marcar la diferencia.',
            cta: 'Respiración guiada',
            icon: '🌱',
        },
    },
    {
        emoji: '🙂',
        label: 'Bien',
        value: 4,
        color: '#7cb342',
        bg: '#f1f8e9',
        insight: {
            message: 'Qué bueno saberlo. Cada día positivo suma.',
            suggestion: 'Anota algo que te haya salido bien hoy — refuerza tu bienestar.',
            cta: 'Mi diario',
            icon: '✨',
        },
    },
    {
        emoji: '😊',
        label: 'Muy bien',
        value: 5,
        color: '#43a047',
        bg: '#e8f5e9',
        insight: {
            message: '¡Excelente! Registrar los buenos momentos fortalece tu salud mental.',
            suggestion: 'Compartir este logro con tu terapeuta en la próxima sesión puede ser muy valioso.',
            cta: 'Ver mis avances',
            icon: '🌟',
        },
    },
];

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------
// Animations (MUI sx @keyframes syntax)
// ---------------------------------------------------------------------------

const ANIM_FADE_UP = {
    '0%': { opacity: 0, transform: 'translateY(10px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
};

const ANIM_POP = {
    '0%': { transform: 'scale(1)' },
    '40%': { transform: 'scale(1.3)' },
    '70%': { transform: 'scale(0.9)' },
    '100%': { transform: 'scale(1.1)' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const DAY_LABELS = ['L', 'M', 'Mi', 'J', 'V', 'S', 'D'];

function getLast7Days() {
    const days = [];
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        days.push(d.toISOString().slice(0, 10));
    }
    return days;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * MoodCheckIn — daily emotional check-in with contextual AI insight.
 *
 * Props:
 *   onMoodSelect  {function(mood)}  optional  Called when user selects a mood
 */
const MoodCheckIn = ({ onMoodSelect }) => {
    const [wellness, wellnessDispatcher] = useWellness();
    const { todayMood, moodHistory, fetching } = wellness;

    const [selected, setSelected] = useState(null);

    // Fetch mood history on mount
    useEffect(() => {
        wellnessDispatcher.fetchMoodHistoryStart(7);
    }, []);

    // Sync selected from backend (todayMood)
    useEffect(() => {
        if (todayMood && todayMood.value) {
            setSelected(todayMood.value);
        }
    }, [todayMood]);

    const handleSelect = (mood) => {
        setSelected(mood);
        wellnessDispatcher.saveMoodStart({ value: mood });
        if (onMoodSelect) onMoodSelect(mood);
    };

    const currentMood = MOODS.find(m => m.value === selected);

    // Build weekly map: date → value
    const weekDays = useMemo(() => getLast7Days(), []);
    const moodByDate = useMemo(() => {
        const map = {};
        (moodHistory || []).forEach((m) => {
            const date = m.date ? new Date(m.date).toISOString().slice(0, 10) : '';
            if (date) map[date] = m.value;
        });
        return map;
    }, [moodHistory]);

    const isSaving = fetching.saveMood.state;

    return (
        <Paper
            elevation={2}
            sx={{
                borderRadius: '20px',
                padding: '20px',
                background: '#fff',
                animation: 'fadeUp 0.4s ease both',
                '@keyframes fadeUp': ANIM_FADE_UP,
            }}
        >
            {/* Question */}
            <Typography
                variant="subtitle1"
                sx={{
                    fontSize: '15px',
                    fontWeight: 600,
                    color: DARKER_TEXT,
                    marginBottom: '16px',
                    letterSpacing: '-0.1px',
                }}
            >
                ¿Cómo te sientes hoy?
            </Typography>

            {/* Mood Row */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '6px',
                }}
            >
                {MOODS.map((mood) => {
                    const isMoodSelected = selected === mood.value;
                    return (
                        <Button
                            key={mood.value}
                            onClick={() => handleSelect(mood.value)}
                            aria-label={mood.label}
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '5px',
                                padding: '10px 4px',
                                border: '2px solid',
                                borderColor: isMoodSelected ? mood.color : 'transparent',
                                borderRadius: '14px',
                                backgroundColor: isMoodSelected ? mood.bg : '#fafafa',
                                color: 'inherit',
                                textTransform: 'none',
                                cursor: 'pointer',
                                transition: 'all 0.18s ease',
                                WebkitTapHighlightColor: 'transparent',
                                '&:active': {
                                    transform: 'scale(0.94)',
                                },
                                ...(isMoodSelected && {
                                    animation: 'pop 0.35s ease',
                                    '@keyframes pop': ANIM_POP,
                                }),
                            }}
                        >
                            {/* Emoji */}
                            <Box
                                component="span"
                                sx={{
                                    fontSize: isMoodSelected ? '28px' : '24px',
                                    lineHeight: 1,
                                    transition: 'font-size 0.18s ease',
                                    display: 'block',
                                }}
                            >
                                {mood.emoji}
                            </Box>

                            {/* Label */}
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '10px',
                                    fontWeight: 500,
                                    color: isMoodSelected ? mood.color : PLACEHOLDER_TEXT,
                                    whiteSpace: 'nowrap',
                                    transition: 'color 0.18s ease',
                                }}
                            >
                                {mood.label}
                            </Typography>
                        </Button>
                    );
                })}
            </Box>

            {/* Insight Box */}
            {currentMood && (
                <Box
                    sx={{
                        marginTop: '16px',
                        padding: '14px 16px',
                        backgroundColor: currentMood.bg,
                        borderRadius: '14px',
                        animation: 'fadeUp 0.35s ease both',
                        '@keyframes fadeUp': ANIM_FADE_UP,
                    }}
                >
                    {/* Message */}
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: DARKER_TEXT,
                            margin: '0 0 4px 0',
                        }}
                    >
                        <Box component="span" sx={{ fontSize: '18px', marginRight: '8px' }}>
                            {currentMood.insight.icon}
                        </Box>
                        {currentMood.insight.message}
                    </Typography>

                    {/* Suggestion */}
                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: '13px',
                            color: DARK_TEXT,
                            margin: 0,
                            lineHeight: 1.5,
                            display: 'block',
                            marginBottom: '10px',
                        }}
                    >
                        {currentMood.insight.suggestion}
                    </Typography>

                    {/* Confirmation */}
                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: '12px',
                            color: PLACEHOLDER_TEXT,
                            textAlign: 'center',
                            display: 'block',
                        }}
                    >
                        {isSaving ? (
                            <CircularProgress size={12} sx={{ mr: 0.5 }} />
                        ) : (
                            'Registro guardado ✓'
                        )}
                    </Typography>
                </Box>
            )}

            {/* Mini weekly calendar */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '14px',
                    gap: '4px',
                }}
            >
                {weekDays.map((dateStr, i) => {
                    const moodVal = moodByDate[dateStr];
                    const moodData = MOODS.find((m) => m.value === moodVal);
                    const isToday = dateStr === new Date().toISOString().slice(0, 10);
                    const dayIndex = new Date(dateStr + 'T12:00:00').getDay();
                    // Convert JS day (0=Sun) to our labels (0=Mon)
                    const labelIdx = dayIndex === 0 ? 6 : dayIndex - 1;
                    return (
                        <Box
                            key={dateStr}
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '2px',
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '10px',
                                    fontWeight: isToday ? 700 : 400,
                                    color: isToday ? DARKER_TEXT : PLACEHOLDER_TEXT,
                                }}
                            >
                                {DAY_LABELS[labelIdx]}
                            </Typography>
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: moodData ? moodData.bg : '#f5f5f5',
                                    border: isToday ? '2px solid' : 'none',
                                    borderColor: isToday ? (moodData?.color || '#ccc') : undefined,
                                    fontSize: moodData ? '14px' : '10px',
                                }}
                            >
                                {moodData ? moodData.emoji : '·'}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
};

export default MoodCheckIn;
